import React, { useState, useEffect } from 'react';
import { FiSearch, FiX, FiKey, FiEye, FiEyeOff, FiToggleLeft, FiToggleRight, FiRefreshCw } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const VendorManagement = () => {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchVendors();
  }, [search]);

  const fetchVendors = async (isManualRefresh = false) => {
    try {
      const params = { approvalStatus: 'approved' };
      if (search) params.search = search;

      if (isManualRefresh) {
        setRefreshing(true);
        toast.loading('Refreshing vendors...', { id: 'refresh' });
      }

      console.log('Fetching vendors with params:', params);
      const response = await api.get('/superadmin/vendors', { params });
      console.log('Vendors response:', response.data);
      
      if (response.data.success) {
        setVendors(response.data.data);
        console.log('Vendors loaded:', response.data.data.length);
        
        // Show success message for manual refresh
        if (isManualRefresh) {
          toast.success(`Refreshed! Found ${response.data.data.length} vendors`, { id: 'refresh' });
        }
      } else {
        toast.error('Failed to load vendors data', { id: 'refresh' });
      }
    } catch (error) {
      console.error('Fetch vendors error:', error);
      toast.error(`Failed to fetch vendors: ${error.response?.data?.message || error.message}`, { id: 'refresh' });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const viewVendorDetails = async (vendorId) => {
    try {
      const response = await api.get(`/superadmin/vendors/${vendorId}/details`);
      if (response.data.success) {
        setSelectedVendor(response.data.data);
        setShowModal(true);
      }
    } catch (error) {
      toast.error('Failed to fetch vendor details');
    }
  };

  const toggleVendorStatus = async (vendorId) => {
    try {
      const response = await api.patch(`/superadmin/vendors/${vendorId}/toggle-status`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchVendors();
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      const response = await api.put(`/superadmin/vendors/${selectedVendor.vendor._id}/password`, {
        newPassword
      });
      if (response.data.success) {
        toast.success('Password updated successfully');
        setShowPasswordModal(false);
        setNewPassword('');
      }
    } catch (error) {
      toast.error('Failed to update password');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50 p-4 md:p-6">
      {/* Header Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Vendor Management</h1>
            <p className="text-gray-600">Manage all approved vendors, credentials, and roles</p>
          </div>
          <button
            onClick={fetchVendors}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
          >
            <FiRefreshCw className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search by restaurant name, email, or contact..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
          />
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Restaurant Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Email</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Contact Number</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Username</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {vendors.map((vendor) => (
                <tr key={vendor._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <div className="font-semibold text-gray-900">{vendor.restaurantDetails.restaurantName}</div>
                    <div className="text-sm text-gray-500">{vendor.restaurantDetails.city}</div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{vendor.personalDetails.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-700">{vendor.personalDetails.contactNumber}</td>
                  <td className="px-6 py-4">
                    <code className="px-2 py-1 bg-gray-100 rounded text-sm text-gray-800">{vendor.ownerCredentials.username}</code>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                      vendor.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {vendor.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <button
                        onClick={() => viewVendorDetails(vendor._id)}
                        title="View Details"
                        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors duration-200"
                      >
                        <FiEye size={16} />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedVendor({ vendor });
                          setShowPasswordModal(true);
                        }}
                        title="Reset Password"
                        className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors duration-200"
                      >
                        <FiKey size={16} />
                      </button>
                      <button
                        onClick={() => toggleVendorStatus(vendor._id)}
                        title="Toggle Status"
                        className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-colors duration-200"
                      >
                        {vendor.isActive ? <FiToggleRight size={16} /> : <FiToggleLeft size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {vendors.length === 0 && !loading && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiSearch className="text-gray-400 text-2xl" />
            </div>
            <p className="text-gray-500 mb-2">No vendors found</p>
            <p className="text-gray-400 text-sm">Try adjusting your search or check vendor approval status</p>
          </div>
        )}
      </div>

      {/* Vendor Details Modal */}
      {showModal && selectedVendor && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-2xl font-bold text-gray-900">Vendor Details</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Personal Details */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Name:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.personalDetails.name}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.personalDetails.email}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contact:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.personalDetails.contactNumber}</span>
                  </div>
                </div>
              </div>

              {/* Restaurant Details */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Restaurant Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Restaurant Name:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.restaurantDetails.restaurantName}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.restaurantDetails.city}</span>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.restaurantDetails.address}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">FSSAI Number:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.restaurantDetails.fssaiNumber}</span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">GST Number:</label>
                    <span className="text-gray-900">{selectedVendor.vendor.restaurantDetails.gstNumber}</span>
                  </div>
                </div>
              </div>

              {/* Credentials */}
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Login Credentials</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Username:</label>
                    <code className="px-2 py-1 bg-gray-200 rounded text-sm text-gray-800">{selectedVendor.vendor.ownerCredentials.username}</code>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Role:</label>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-semibold">Vendor</span>
                  </div>
                </div>
              </div>

              {/* Captains */}
              {selectedVendor.captains && selectedVendor.captains.length > 0 && (
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Captains ({selectedVendor.captains.length})</h3>
                  <div className="space-y-3">
                    {selectedVendor.captains.map((captain) => (
                      <div key={captain._id} className="bg-white rounded-lg p-4 flex justify-between items-center">
                        <div>
                          <div className="font-semibold text-gray-900">{captain.name}</div>
                          <div className="text-sm text-gray-600 mt-1">
                            <div>Username: <code className="px-2 py-1 bg-gray-100 rounded text-xs text-gray-800">{captain.username}</code></div>
                            <div>{captain.email} | {captain.contactNumber}</div>
                          </div>
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          captain.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {captain.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Modal */}
      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">Reset Vendor Password</h2>
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                <FiX size={20} />
              </button>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">New Password</label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    className="w-full px-4 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 flex gap-3">
              <button 
                onClick={() => setShowPasswordModal(false)}
                className="flex-1 px-4 py-2 bg-gray-500 text-white rounded-lg font-medium hover:bg-gray-600 transition-colors duration-200"
              >
                Cancel
              </button>
              <button 
                onClick={handlePasswordReset}
                className="flex-1 px-4 py-2 bg-purple-500 text-white rounded-lg font-medium hover:bg-purple-600 transition-colors duration-200"
              >
                Reset Password
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorManagement;
